import * as Cookies from 'js-cookie';
import TagManager from 'react-gtm-module';
import { call, takeLatest } from 'redux-saga/effects';

import type * as authActions from '~/actions/auth.actions';
import type User from '~/types/User';

import { ActionKeys } from '~/actions/constants';
import { SHIPPO_LASTSEEN_COOKIE_NAME } from '~/constants/cookies';
import { type GTMDataLayer } from '~/types/GTM';
import { getFullName } from '~/types/User';

const getIsImpersonateCookie = () => Cookies.get('is_impersonate') || 'false';

export function* isImpersonating() {
  const impersonatingCookieVal: string = yield call(getIsImpersonateCookie);
  return impersonatingCookieVal === 'true';
}

function* createDataLayer(currentUser: User) {
  const gtmDataLayer: GTMDataLayer = {
    availableCarriers: [],
    dateJoined: currentUser.dateJoined.toISOString(),
    event: 'USER_DATA_POPULATED',
    groupName: currentUser.groupName || '0',
    isImpersonating: yield call(isImpersonating),
    lifetimeVolSegmentMax: currentUser.lifetimeVolSegmentMax || 0,
    lifetimeVolSegmentMin: currentUser.lifetimeVolSegmentMin || 0,
    loginUserId: currentUser.loginUserId,
    revenueTierName: currentUser.revenueTierName || '5',
    storesEnabled: [],
    userEmail: currentUser.email,
    userFullName: getFullName(currentUser),
    userId: currentUser.id,
  };

  return gtmDataLayer;
}

export const getGTMConfig = () => {
  const { auth, dataLayer: dataLayerName, enabled, id: gtmId, preview } = CONFIG.gtm;
  return { auth, dataLayerName, enabled, gtmId, preview };
};

export function* initializeAnonymousGTM() {
  let anonymousGTMTriggered = true;
  const gtmConfig: ReturnType<typeof getGTMConfig> = yield call(getGTMConfig);
  if (!gtmConfig.enabled) return;

  while (anonymousGTMTriggered) {
    const tagManagerArgs = { ...gtmConfig };
    yield call([TagManager, TagManager.initialize], tagManagerArgs);
    anonymousGTMTriggered = false;
  }
}

export function* initializeGTM(action: ReturnType<typeof authActions.userDidAuthenticate>) {
  const { currentUser } = action.payload;

  // Set a last seen cookie for user identification on marketing site
  const isUserImpersonating: boolean = yield call(isImpersonating);
  if (!isUserImpersonating) {
    const today = new Date();
    yield call(
      Cookies.set,
      SHIPPO_LASTSEEN_COOKIE_NAME,
      JSON.stringify({
        availableCarriers: [],
        dateJoined: currentUser.dateJoined.toISOString().substring(0, 10),
        id: currentUser.id,
        lastSeen: today.toISOString().substring(0, 10),
        lifetimeVolSegmentMax: currentUser.lifetimeVolSegmentMax || 0,
        lifetimeVolSegmentMin: currentUser.lifetimeVolSegmentMin || 0,
        revenueTierName: currentUser.revenueTierName || '5',
        storesEnabled: [],
      }),
      {
        domain: CONFIG.shippoRootUrl,
        expires: 365,
      },
    );
  }

  const gtmConfig: ReturnType<typeof getGTMConfig> = yield call(getGTMConfig);

  if (!gtmConfig.enabled) return;

  const dataLayer: GTMDataLayer = yield call(createDataLayer, currentUser);
  const tagManagerArgs = { ...gtmConfig, dataLayer };

  yield call([TagManager, TagManager.initialize], tagManagerArgs);
}

export default [
  takeLatest(ActionKeys.INITIALIZE_ANONYMOUS_TAG_MANAGER, initializeAnonymousGTM),
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, initializeGTM),
];
